import React from "react";
import { TOS_LINK, PRIVACY_LINK } from "Constants";
import { ModalForm } from "collections/Modal";
import { Field, Checkbox, Form } from "collections/Form";
import { useTranslation } from "utilities/i18n";
import { useSelector } from "react-redux";
import { State } from "store";
import { formatISO } from "date-fns";
import { useUserMeta } from "utilities/userMeta";
import * as yup from "yup";
import { InferType } from "yup";
import { Animation, SubmitButton } from "elements";
import welcomeAstronautLight from "assets/animations/Welcome-Modal-Light.json";
import welcomeAstronautDark from "assets/animations/Welcome-Modal-Dark.json";

const schema = yup.object({
  agreed: yup
    .boolean()
    .required()
    .test("must be accepted", "must accept tos", value => value === true)
});

type WelcomeType = InferType<typeof schema>;

export const WelcomeModalLoader: React.FC = () => {
  const [setUserMeta] = useUserMeta();

  const tos = useSelector((state: State) => {
    return state.userMeta?.acceptedToS;
  });
  const { t } = useTranslation();

  if (tos) {
    return <></>;
  }

  const onSubmit = (v: WelcomeType) => {
    setUserMeta({
      acceptedToS: formatISO(new Date(), { representation: "date" }).replace(
        /_/g,
        "_"
      )
    });
  };

  return (
    <ModalForm<WelcomeType>
      name="WelcomeModal"
      title=""
      initialValues={{
        agreed: false
      }}
      validationSchema={schema}
      validateOnMount
      validateOnChange
      closeOnDimmerClick={false}
      fluid
      className="welcome-modal p-2 overflow"
      onSubmit={onSubmit}
    >
      {({ isSubmitting, isValid, field }) => (
        <div>
          <div
            data-testid="welcome-modal"
            className="pt-5 pr-5 scrollable-content"
          >
            <Animation
              animation={{
                light: welcomeAstronautLight,
                dark: welcomeAstronautDark
              }}
              className="mb-0"
            />
            <h4>{t("WELCOME_MODAL_TITLE")}</h4>
            <p className="mb-5">{t("WELCOME_MODAL_DESCRIPTION")}</p>
            <p className="main-heading mb-0">{t("WELCOME_MODAL_H0")}</p>
            <div className="pl-1">
              <h6>{t("WELCOME_MODAL_H1_0")}</h6>
              <p>{t("WELCOME_MODAL_P_0")}</p>
              <h6>{t("WELCOME_MODAL_H1_1")}</h6>
              <p>{t("WELCOME_MODAL_P_1")}</p>
              <h6>{t("WELCOME_MODAL_H1_2")}</h6>
              <p>{t("WELCOME_MODAL_P_2")}</p>
            </div>
          </div>
          <Form>
            <label className="checkbox-label mt-2 pl-25">
              <Field name={field.agreed.name} as={Checkbox} />
              <p className="pt-0">
                {t("WELCOME_MODAL_AGREE_0") + " "}
                <a href={TOS_LINK} target="_blank" rel="noopener noreferrer">
                  {t("WELCOME_MODAL_AGREE_TERMS")}
                </a>{" "}
                {t("WELCOME_MODAL_AGREE_1") + " "}
                <a
                  href={PRIVACY_LINK}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {t("WELCOME_MODAL_AGREE_PRIVACY")}
                </a>
                .
              </p>
            </label>
            <div className="btn-container">
              <SubmitButton
                isSubmitting={isSubmitting}
                testId="welcome-modal-button"
                disabled={!isValid}
                labelKey="WELCOME_MODAL_SUBMIT"
                submittingLabelKey="WELCOME_MODAL_SUBMIT"
              />
            </div>
          </Form>
        </div>
      )}
    </ModalForm>
  );
};

export default WelcomeModalLoader;
